import { useForm } from '@mantine/form';
import { Button, Group, Modal, Stack, MultiSelect, Box } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconArrowRight, IconCalendar } from '@tabler/icons-react';

import type { BriefParkingLot } from '../../../models/parking-contract.ts';

interface SetupSpecifyParkingLotsModalProps {
  opened: boolean;
  onClose: () => void;
  initialValues: SetupSpecifyParkingLotsForm;
  parkingLotData: BriefParkingLot[];
  onConfirm: (formValues: SetupSpecifyParkingLotsForm) => void;
  isPending: boolean;
  allowEditTime: boolean;
  allowBlankParkingLot: boolean;
}

export interface SetupSpecifyParkingLotsForm {
  activeStartAt: string;
  activeEndAt: string;
  parkingLotIds: string[];
}

export default function SetupSpecifyParkingLotsModal({
  opened,
  onClose,
  initialValues,
  parkingLotData,
  onConfirm,
  isPending,
  allowEditTime,
  allowBlankParkingLot,
}: SetupSpecifyParkingLotsModalProps) {
  const form = useForm<SetupSpecifyParkingLotsForm>({
    mode: 'controlled',
    initialValues: { ...initialValues },
    validate: {
      activeStartAt: (value, values) => {
        return value ? checkStartEndRange(value, values.activeEndAt) : ' ';
      },
      activeEndAt: (value, values) => {
        return value ? checkStartEndRange(values.activeStartAt, value) : ' ';
      },
      parkingLotIds: (value) => {
        if (!allowBlankParkingLot && value.length === 0) {
          return '請至少選擇一個場站';
        }
      },
    },
  });

  const parkingLotSelections = parkingLotData.map((parkingLot) => {
    return {
      value: parkingLot.id,
      label: parkingLot.name,
    };
  });

  // functions
  function checkStartEndRange(startAt, endAt) {
    if (!startAt || !endAt) return ' ';
    if (new Date(startAt) > new Date(endAt)) return ' ';
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="新增指定場站"
      centered
      styles={{ title: { fontWeight: 700, fontSize: 16 } }}
    >
      <form
        onSubmit={form.onSubmit((values: SetupSpecifyParkingLotsForm) =>
          onConfirm(values),
        )}
      >
        <Stack gap="lg">
          <Group>
            <DatePickerInput
              withAsterisk
              label="指定場站合約生效起迄日"
              rightSection={<IconCalendar size={14} />}
              rightSectionPointerEvents="none"
              style={{ flex: 1 }}
              disabled={!allowEditTime}
              placeholder="請選擇起始日"
              valueFormat="YYYY/MM/DD"
              key={form.key('activeStartAt')}
              {...form.getInputProps('activeStartAt')}
            />
            <Stack>
              <Box h={10} /> {/* 塞個東東讓下面的箭頭位置正確 */}
              <IconArrowRight />
            </Stack>
            <DatePickerInput
              label=" "
              rightSection={<IconCalendar size={14} />}
              rightSectionPointerEvents="none"
              style={{ flex: 1 }}
              disabled={!allowEditTime}
              placeholder="請選擇結束日"
              valueFormat="YYYY/MM/DD"
              key={form.key('activeEndAt')}
              {...form.getInputProps('activeEndAt')}
            />
          </Group>
          <MultiSelect
            searchable
            label="指定場站"
            placeholder="請選擇停車場"
            data={parkingLotSelections}
            key={form.key('parkingLotIds')}
            {...form.getInputProps('parkingLotIds')}
          />
          <Group justify="flex-end">
            <Button type="button" onClick={onClose} variant="outline" fw={400}>
              取消
            </Button>
            <Button type="submit" loading={isPending} fw={400}>
              更新
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
